import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { marketFromPath } from "@/lib/marketRoutes";
import { useSession } from "@/context/SessionContext";

const upsertHead = (selector, tag, attributes) => {
  let node = document.head.querySelector(selector);
  if (!node) {
    node = document.createElement(tag);
    document.head.appendChild(node);
  }
  Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, value));
};

const removeMarketSeoHead = () => {
  document.head.querySelector('meta[name="description"]')?.remove();
  document.head.querySelector('link[rel="canonical"]')?.remove();
  document.head.querySelectorAll('meta[property^="og:"], meta[name^="twitter:"]').forEach((node) => node.remove());
};

export default function MarketSeo() {
  const { pathname } = useLocation();
  const { ready, seo, activeMarkets } = useSession();
  const market = marketFromPath(pathname);
  const marketCountryId = market?.countryId || "";

  useEffect(() => {
    // Samme regel for alle markeder: kun et aktivt land med SEO-profil må
    // udstede landespecifikke SEO/OG-tags og blive indekseret.
    const canIndex = Boolean(market && ready && seo?.country_active);
    upsertHead('meta[name="robots"]', "meta", { name: "robots", content: canIndex ? "index,follow" : "noindex,follow" });
    document.documentElement.lang = market?.languageId || "da";
    if (canIndex) {
      document.title = seo.title;
      upsertHead('meta[name="description"]', "meta", { name: "description", content: seo.description });
      upsertHead('meta[property="og:title"]', "meta", { property: "og:title", content: seo.og_title });
      upsertHead('meta[property="og:description"]', "meta", { property: "og:description", content: seo.og_description });
      upsertHead('meta[property="og:type"]', "meta", { property: "og:type", content: "website" });
      upsertHead('meta[property="og:image"]', "meta", { property: "og:image", content: new URL(seo.og_image, window.location.origin).href });
      upsertHead('meta[property="og:locale"]', "meta", { property: "og:locale", content: seo.og_locale });
    } else {
      document.title = "InvictusA";
      removeMarketSeoHead();
    }

    document.head.querySelectorAll('link[data-market-alternate="true"]').forEach((node) => node.remove());
    if (!canIndex) return;

    const suffix = pathname.replace(/^\/(dk|se|gb)(?=\/|$)/i, "") || "/";
    const canonical = `${window.location.origin}${pathname.endsWith("/") || pathname !== `/${market.key}` ? pathname : `${pathname}/`}`;
    upsertHead('link[rel="canonical"]', "link", { rel: "canonical", href: canonical });
    upsertHead('meta[property="og:url"]', "meta", { property: "og:url", content: canonical });
    if (!canIndex) return;
    activeMarkets.forEach((item) => {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = item.hreflang;
      link.href = `${window.location.origin}/${item.prefix}${suffix}`;
      link.dataset.marketAlternate = "true";
      document.head.appendChild(link);
    });
  }, [activeMarkets, market, marketCountryId, pathname, ready, seo]);

  return null;
}